/**
 * Has attachments / images / comments on the tasks list (GET /tasks
 * `has_attachments=1` and so on). Kept apart from tasks-query so it loads
 * without the API client.
 */

const CONTENT_FILTERS_KEY = "tasks-content-filters"

/** Server-side filters on what a task carries; each is sent as `<key>=1`. */
export type TaskContentFilter = "has_attachments" | "has_images" | "has_comments"

export const TASK_CONTENT_FILTERS: { key: TaskContentFilter; label: string }[] = [
  { key: "has_attachments", label: "Has attachments" },
  { key: "has_images", label: "Has images" },
  { key: "has_comments", label: "Has comments" },
]

/** The saved content filters, in their fixed order; unknown values dropped. */
export function readSavedContentFilters(storage: Pick<Storage, "getItem"> = localStorage): TaskContentFilter[] {
  try {
    const saved: unknown = JSON.parse(storage.getItem(CONTENT_FILTERS_KEY) ?? "[]")
    if (!Array.isArray(saved)) return []
    return TASK_CONTENT_FILTERS.map((f) => f.key).filter((key) => saved.includes(key))
  } catch {
    return []
  }
}

export function saveContentFilters(filters: TaskContentFilter[], storage: Pick<Storage, "setItem"> = localStorage) {
  storage.setItem(CONTENT_FILTERS_KEY, JSON.stringify(filters))
}

/**
 * `{ has_images: 1, … }` for the active filters. Inactive ones are left out
 * rather than sent as 0, so the query key with none on is the same key the
 * cold-open prefetch warms.
 */
export function contentFilterParams(filters: TaskContentFilter[]): Partial<Record<TaskContentFilter, 1>> {
  const params: Partial<Record<TaskContentFilter, 1>> = {}
  for (const key of filters) params[key] = 1
  return params
}
